// Package imports:
import React from 'react';
// Component imports:
import LmdTable from '../../../ui-elements/Table/LmdTable';
import Link from '../../../ui-elements/Link/Link';
import CalculatedColoredNumber from '../../../ui-elements/CalculatedColoredNumber/CalculatedColoredNumber';
import Avatar, { AvatarNames } from '../../../ui-elements/Avatar/Avatar';
import MiniGraph from '../../../ui-elements/MiniGraph/MiniGraph';
// Service imports:
import { convertToPercentage, displayQuantityShorthand, formatNumber, getLogoSymbol } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import { IApiLmdSnapshot } from '../../../types/HlutabrefTypes';

type IProps = IDefaultProps;

const ETF: React.FC<IProps>= ({
    refreshRateMs,
    accessToken,
}) => {
    const [ etf, prevEtf ] = useApiLmdData<IApiLmdSnapshot[]>(
        '/v1/market_data/v1/keldan/exchange/XICE/market/ISEQ%20XFU/symbol/*/delayed_equities',
        accessToken,
        refreshRateMs
    );

    return (
        <div className='KCL_ETF'>
            <LmdTable
                apiData={etf}
                columns={[{
                    title: 'Auðkenni',
                    dataAccessor: ({symbol}) => (
                        <div className="avatar-and-link avatar-text-link">
                            <a href={`/Markadir/Hlutabref/${symbol}`} aria-label={`Hlutabréf - ${symbol}`}>
                                {(symbol === null)
                                    ? null
                                    : (
                                        <Avatar
                                            size='sm'
                                            backgroundGrey
                                            name={getLogoSymbol(symbol.toUpperCase()) as AvatarNames}
                                        />
                                    )
                                }
                            </a>
                            <Link url={`/Markadir/Hlutabref/${symbol}`} linkSize='13'>
                                {symbol}
                            </Link>
                        </div>
                    ),
                    textAlign: 'left',
                    simpleSortable: ({symbol}) => symbol
                }, {
                    title: 'Verð',
                    dataAccessor: ({last_price, symbol}) => {
                        return (
                            <td style={{ padding: 0 }}>
                                <CalculatedColoredNumber 
                                    hasBackground
                                    currentValue={last_price}
                                    previousValue={prevEtf.data?.find(pd => pd.symbol === symbol)?.last_price ?? null}
                                >
                                    {formatNumber(last_price)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({last_price}) => last_price
                }, {
                    title: 'Breyting',
                    dataAccessor: ({intraday_per_change, symbol}) => {
                        return (
                            <td style={{ padding: 0 }}>
                                <CalculatedColoredNumber
                                    hasColor
                                    hasBackground
                                    currentValue={intraday_per_change}
                                    previousValue={prevEtf.data?.find(pd => pd.symbol === symbol)?.intraday_per_change ?? null}
                                >
                                    {convertToPercentage(intraday_per_change, true)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({intraday_per_change}) => intraday_per_change
                }, {
                    title: 'Sl. 3 Mán',
                    dataAccessor: ({symbol}) => (symbol) && <MiniGraph folder='Funds' symbol={symbol} />
                }, {
                    title: 'Kaup',
                    dataAccessor: ({bid, symbol}) => {
                        return (
                            <td style={{ padding: 0 }}>
                                <CalculatedColoredNumber
                                    hasBackground
                                    currentValue={bid}
                                    previousValue={prevEtf.data?.find(pd => pd.symbol === symbol)?.bid ?? null}>
                                    {formatNumber(bid)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({bid}) => bid
                }, {
                    title: 'Sala',
                    dataAccessor: ({ask, symbol}) => {
                        return (
                            <td style={{ padding: 0}}>
                                <CalculatedColoredNumber
                                    hasBackground
                                    currentValue={ask}
                                    previousValue={prevEtf.data?.find(pd => pd.symbol === symbol)?.ask ?? null}
                                    >
                                    {formatNumber(ask)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({ask}) => ask
                }, {
                    title: 'Velta',
                    dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                    simpleSortable: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover
                }]}
            />
        </div>
    );
}

export default ETF;